import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';

// La chiusura degli avvisi del builder è una preferenza di presentazione, non un
// dato di sessione: sopravvive al logout, altrimenti il TD si ritrova l'avviso
// ogni volta che rientra. Tutto il resto del localStorage va comunque svuotato.
const PRESERVED_PREFIXES = ['p2b:builder:exceeds-warning-dismissed:'];

const clearLocalStorageExceptPreferences = () => {
  try {
    const preserved: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !PRESERVED_PREFIXES.some(prefix => key.startsWith(prefix))) continue;
      const value = localStorage.getItem(key);
      if (value !== null) preserved.push([key, value]);
    }
    localStorage.clear();
    preserved.forEach(([key, value]) => localStorage.setItem(key, value));
  } catch {
    // Storage non disponibile (Safari privato): non c'è nulla da preservare né da pulire.
  }
};

export const useLogout = () => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const logout = async (redirectTo: string = '/login') => {
    if (isLoggingOut) return; // Prevent multiple logout attempts
    
    setIsLoggingOut(true);
    
    try {
      // Clear all React Query cache first
      await queryClient.clear();
      
      // Clear localStorage
      clearLocalStorageExceptPreferences();
      
      // Sign out from Supabase
      await signOut();
      
    } catch (error) {
      console.error('Logout error:', error);
      
      // Even if there's an error, clear localStorage and try to navigate to login
      clearLocalStorageExceptPreferences();
      
      toast({
        title: "Errore durante il logout",
        description: "Si è verificato un errore, ma sei stato disconnesso.",
        variant: "destructive",
      });
    } finally {
      setIsLoggingOut(false);
      navigate(redirectTo, { replace: true });
    }
  };

  return {
    logout,
    isLoggingOut
  };
}; 